import { Row } from '@devexpress/dx-react-grid';
import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { useRSAKey2KeyReport } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDateTime } from 'utils/helpers';
import { AxiosError } from 'axios';
import { createReportURL } from 'core/routes';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import {
  RSA_KEY2KEY_REPORT_MESSAGE,
  RSA_KEY2KEY_REPORT_NAME,
  RSA_KEY2KEY_REPORT_TITLE,
  columns,
  selectOptions,
} from './constants';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { ReportFormKey2KeyReport } from 'components/ReportForms/ReportFormKey2Key';
import { valuesConverter } from './helpers';
import { SelectOption } from 'components/types';
import { ReportProps } from 'pages/ReportsDetails/types';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import DevExtremeReportGrid from 'components/ReportGrid_New';

export function RSAKey2KeyReport({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    year: urlParams.get('year'),
  };

  const queryEnabled = !!urlParamsValue.year;

  const { isReportFetching, refetchReport, reportData } = useRSAKey2KeyReport(
    urlParamsValue.year,
    queryEnabled,
    handleError
  );

  const rows = useMemo<Row[] | undefined>(() => reportData ? valuesConverter(reportData) : undefined, [reportData]);

  const [year, setYear] = useState<SelectOption[]>([]);

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParamsValue.year]);

  useEffect(() => {
    if (urlParamsValue.year != year?.map((e) => e.value)?.join(','))
      setYear(
        selectOptions?.filter((e) =>
          urlParamsValue.year?.split(',').includes(e.value)
        ) || []
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportHeaderTagLine = useMemo(
    () =>
      `Report produced on ${formatDateTime(new Date())} for the year ${year?.map(({ text }) => text)?.join(', ')}`,
    [year]
  );

  return (
    <MainPageContainer isLoading={isReportFetching} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={RSA_KEY2KEY_REPORT_NAME}
          infoMessage={RSA_KEY2KEY_REPORT_MESSAGE}
        />
        <ReportFormKey2KeyReport
          handleSubmit={handleSubmit}
          isBusy={isReportFetching}
          setYear={setYear}
          year={year}
        />
      </UpperInfoContainer>

      {!isReportFetching && rows?.length === 0 && <NoReportDataMessage />}

      <ReportGridContainer direction="column">
        <DevExtremeReportGrid
          columns={columns}
          headerTitleForSheet={RSA_KEY2KEY_REPORT_NAME}
          isLoading={isReportFetching}
          pdfOrientation='l'
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          title={RSA_KEY2KEY_REPORT_TITLE}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (urlParamsValue.year == year?.map((e) => e.value)?.join(','))
      refetchReport();
    else navigateIfPossible();
  }

  function navigateIfPossible() {
    if (year != null)
      navigate(
        createReportURL(
          { year: year?.map((e) => e.value)?.join(',') },
          reportPath,
        ), { replace: true });
  }

  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports('RSA Key2Key Report'));
    setToast(true);
  }
}
